import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Загрузка данных экрана.
 *
 * Состояние — размеченное объединение, а не тройка `data | loading | error`. Причина
 * практическая: с тремя независимыми полями возможна комбинация «загрузка закончилась,
 * данных нет, ошибки нет», и экран в ней рисует пустой список вместо сообщения об отказе —
 * ровно тот тихий фолбэк, который запрещает AGENTS.md.
 */
export type AsyncState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: T }
  | { readonly status: 'failed'; readonly error: unknown };

export interface AsyncResult<T> {
  readonly state: AsyncState<T>;
  reload(): void;
}

export function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  // Гонка ответов: пользователь переключает день быстрее, чем отвечает сервер, и ответ
  // на предыдущий день приходит последним. Без счётчика экран показал бы слоты не того дня.
  const latest = useRef(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    let alive = true;

    setState({ status: 'loading' });
    loadRef
      .current()
      .then((data) => {
        if (alive && latest.current === ticket) setState({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (alive && latest.current === ticket) setState({ status: 'failed', error });
      });

    return () => {
      alive = false;
    };
    // Зависимости задаёт вызывающий: сама `load` меняется на каждом рендере (замыкание),
    // и включи мы её сюда, экран перезапрашивал бы данные бесконечно.
  }, [...deps, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  return { state, reload };
}
